import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import {
  Plus, Edit, Trash2, Eye, Search, Globe, Briefcase, Target, Zap,
  Calendar, Linkedin, Sparkles, Copy, X, Loader2, ChevronLeft, ChevronRight,
  Image, Download, Upload, RefreshCw, Filter, BarChart3, Clock, Award,
  FileText, Check, AlertTriangle, BookOpen, Settings, Grid, List, Rocket
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Constants
const COUNTRIES = ["India", "USA", "UK", "EMEA", "Australia"];
const ROLE_CATEGORIES = ["IT", "Healthcare", "Life Sciences", "Pharma", "Engineering", "Professional"];
const SOURCING_THEMES = [
  "Foundations", "Boolean Mastery", "Passive Talent", "Communities",
  "Social Sourcing", "X-Ray Search", "Tools & Automation", "Talent Mapping",
  "Competitor Intelligence", "Diversity Sourcing", "Niche Platforms", "Advanced Techniques"
];
const DIFFICULTY_LEVELS = ["Beginner", "Intermediate", "Advanced"];
const TARGET_AUDIENCES = ["Recruiter", "Sourcer", "TA Leader", "HR Manager"];
const LINKEDIN_STATUSES = ["draft", "scheduled", "published", "reposted"];

const countryFlags = {
  'India': '🇮🇳', 'USA': '🇺🇸', 'UK': '🇬🇧', 'EMEA': '🌍', 'Australia': '🇦🇺'
};

const SourcingKnowledgeManager = ({ auth }) => {
  const { toast } = useToast();
  
  // View state
  const [viewMode, setViewMode] = useState('list'); // list, calendar, create, edit
  const [selectedTechnique, setSelectedTechnique] = useState(null);
  
  // Data state
  const [techniques, setTechniques] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [filterCountry, setFilterCountry] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLinkedInStatus, setFilterLinkedInStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // Seeding state
  const [seeding, setSeeding] = useState(false);

  // Load data
  useEffect(() => {
    loadTechniques();
    loadStats();
  }, []);
  
  // Seed production content (Jan 1-23)
  const handleSeedProduction = async () => {
    if (!window.confirm('This will generate 17 expert-level sourcing posts for Jan 1-23 and publish them immediately. Continue?')) {
      return;
    }
    
    try {
      setSeeding(true);
      const res = await axios.post(`${BACKEND_URL}/api/sourcing/admin/seed-production`, {
        start_date: "2025-01-01",
        end_date: "2025-01-23"
      }, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      
      if (res.data.success) {
        toast({
          title: "Content Seeded Successfully!",
          description: `Created ${res.data.created} expert sourcing posts (${res.data.date_range?.start} to ${res.data.date_range?.end})`,
        });
        loadTechniques();
        loadStats();
      } else if (res.data.error === "Seed content already exists") {
        toast({
          title: "Content Already Seeded",
          description: `${res.data.existing_count} posts already exist. Delete them from the list first if you want to re-seed.`,
          variant: "default"
        });
      } else {
        toast({
          title: "Seeding Issue",
          description: res.data.message || "Something went wrong",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Seeding Failed",
        description: error.response?.data?.detail || "Failed to seed production content",
        variant: "destructive"
      });
    } finally {
      setSeeding(false);
    }
  };

  // Seed extended content (Jan 24 - Jan 31) - Scheduled posts
  const [seedingExtended, setSeedingExtended] = useState(false);
  
  const handleSeedExtended = async () => {
    if (!window.confirm('This will generate 6 diverse sourcing posts (Jan 24-31) including Veteran Hiring, Refugee/DEI, Neurodiversity, Career Changers, Emerging Markets, and Internal Mobility. They will be SCHEDULED to auto-publish at 12:30 AM IST. Continue?')) {
      return;
    }
    
    try {
      setSeedingExtended(true);
      const res = await axios.post(`${BACKEND_URL}/api/sourcing/admin/seed-extended`, {
        start_date: "2025-01-24",
        end_date: "2025-01-31"
      }, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      
      if (res.data.success) {
        toast({
          title: "Extended Content Scheduled!",
          description: `Scheduled ${res.data.created} diverse sourcing posts (${res.data.date_range?.start} to ${res.data.date_range?.end}) for auto-publish at 12:30 AM IST`,
        });
        loadTechniques();
        loadStats();
      } else if (res.data.error === "Extended content already exists") {
        toast({
          title: "Extended Content Already Exists",
          description: `${res.data.existing_count} posts already scheduled. Delete them first to re-seed.`,
          variant: "default"
        });
      }
    } catch (error) {
      toast({
        title: "Seeding Failed",
        description: error.response?.data?.detail || "Failed to seed extended content",
        variant: "destructive"
      });
    } finally {
      setSeedingExtended(false);
    }
  };

  // Seed February 2026 content - Fresh, unique, creative techniques
  const [seedingFeb2026, setSeedingFeb2026] = useState(false);
  
  const handleSeedFeb2026 = async () => {
    if (!window.confirm('This will generate 10 fresh, unique, creative sourcing techniques for February 2026. Posts up to today will be PUBLISHED, future dates will be SCHEDULED. Continue?')) {
      return;
    }
    
    try {
      setSeedingFeb2026(true);
      const res = await axios.post(`${BACKEND_URL}/api/sourcing/admin/seed-feb-2026`, {
        start_date: "2026-02-02",
        end_date: "2026-02-13"
      }, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      
      if (res.data.success) {
        toast({
          title: "February 2026 Content Created!",
          description: `Created ${res.data.created} posts: ${res.data.published} published, ${res.data.scheduled} scheduled`,
        });
        loadTechniques();
        loadStats();
      } else if (res.data.error) {
        toast({
          title: "Content Already Exists",
          description: res.data.message,
          variant: "default"
        });
      }
    } catch (error) {
      toast({
        title: "Seeding Failed",
        description: error.response?.data?.detail || "Failed to seed Feb 2026 content",
        variant: "destructive"
      });
    } finally {
      setSeedingFeb2026(false);
    }
  };

  // Update all past-dated content to published
  const [seedingCurrentDate, setSeedingCurrentDate] = useState(false);
  
  const handleSeedToCurrentDate = async () => {
    if (!window.confirm('This will publish all past-dated scheduled posts. Any content dated before today will become PUBLISHED. Continue?')) {
      return;
    }
    
    try {
      setSeedingCurrentDate(true);
      const res = await axios.post(`${BACKEND_URL}/api/sourcing/admin/seed-to-current-date`, {}, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      
      if (res.data.success) {
        toast({
          title: "Content Updated!",
          description: res.data.message + ` (${res.data.posts_updated_to_published || 0} posts updated)`,
        });
        loadTechniques();
        loadStats();
      } else {
        toast({
          title: "No Content Found",
          description: res.data.message,
          variant: "default"
        });
      }
    } catch (error) {
      toast({
        title: "Update Failed",
        description: error.response?.data?.detail || "Failed to update content",
        variant: "destructive"
      });
    } finally {
      setSeedingCurrentDate(false);
    }
  };

  // Seed March 2026 content - 20 advanced techniques across 4 weeks
  const [seedingMarch2026, setSeedingMarch2026] = useState(false);

  const handleSeedMarch2026 = async () => {
    if (!window.confirm('This will generate 20 advanced sourcing techniques for March 2026 (4 weeks). Posts dated up to today will be PUBLISHED, future dates will be SCHEDULED. Continue?')) {
      return;
    }
    try {
      setSeedingMarch2026(true);
      const res = await axios.post(`${BACKEND_URL}/api/sourcing/admin/seed-march-2026`, {
        start_date: "2026-03-02",
        end_date: "2026-03-27"
      }, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      if (res.data.success) {
        toast({
          title: "March 2026 Content Created!",
          description: `Created ${res.data.created} posts: ${res.data.published} published, ${res.data.scheduled} scheduled`,
        });
        loadTechniques();
        loadStats();
      } else if (res.data.error) {
        toast({
          title: "Content Already Exists",
          description: res.data.message,
          variant: "default"
        });
      }
    } catch (error) {
      toast({
        title: "Seeding Failed",
        description: error.response?.data?.detail || "Failed to seed March 2026 content",
        variant: "destructive"
      });
    } finally {
      setSeedingMarch2026(false);
    }
  };

  // Re-balance publish/scheduled status across ALL posts based on their date
  const [rebalancing, setRebalancing] = useState(false);

  const handleRebalance = async () => {
    if (!window.confirm('This re-balances ALL posts: past/today dates become PUBLISHED, future dates become SCHEDULED. Safe to run anytime. Continue?')) {
      return;
    }
    try {
      setRebalancing(true);
      const res = await axios.post(`${BACKEND_URL}/api/sourcing/admin/rebalance-schedule`, {}, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      if (res.data.success) {
        const t = res.data.current_totals || {};
        toast({
          title: "Schedule Re-balanced!",
          description: `Now ${t.published || 0} published, ${t.scheduled || 0} scheduled (${res.data.changed_to_scheduled || 0} → scheduled, ${res.data.changed_to_published || 0} → published)`,
        });
        loadTechniques();
        loadStats();
      }
    } catch (error) {
      toast({
        title: "Re-balance Failed",
        description: error.response?.data?.detail || "Failed to re-balance schedule",
        variant: "destructive"
      });
    } finally {
      setRebalancing(false);
    }
  };

  // One-click master sync: seed latest curriculum + re-balance schedule + clean blog titles
  const [syncing, setSyncing] = useState(false);

  const handleSyncToToday = async () => {
    if (!window.confirm('SYNC TO TODAY will: (1) seed March 2026 content if missing, (2) re-balance publish/scheduled status by date, and (3) clean date prefixes from blog titles. Safe to run after every deploy. Continue?')) {
      return;
    }
    try {
      setSyncing(true);
      const headers = { 'Authorization': `Basic ${auth}` };
      const results = [];

      // 1. Seed March 2026 (idempotent - skips if already present)
      try {
        const seedRes = await axios.post(`${BACKEND_URL}/api/sourcing/admin/seed-march-2026`, {
          start_date: "2026-03-02", end_date: "2026-03-27"
        }, { headers });
        results.push(seedRes.data.success ? `March: +${seedRes.data.created} new` : 'March: already present');
      } catch (e) { results.push('March: skipped'); }

      // 2. Re-balance schedule
      const rebRes = await axios.post(`${BACKEND_URL}/api/sourcing/admin/rebalance-schedule`, {}, { headers });
      const tt = rebRes.data.current_totals || {};
      results.push(`Schedule: ${tt.published || 0} published / ${tt.scheduled || 0} scheduled`);

      // 3. Clean blog titles
      const blogRes = await axios.post(`${BACKEND_URL}/api/blog/admin/clean-titles`, {}, { headers });
      results.push(`Blog: ${blogRes.data.updated || 0} titles cleaned`);

      toast({
        title: "Synced to Today!",
        description: results.join(' · '),
      });
      loadTechniques();
      loadStats();
    } catch (error) {
      toast({
        title: "Sync Failed",
        description: error.response?.data?.detail || "One of the sync steps failed",
        variant: "destructive"
      });
    } finally {
      setSyncing(false);
    }
  };

  const loadTechniques = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterCountry) params.set('country', filterCountry);
      if (filterStatus) params.set('status', filterStatus);
      if (filterLinkedInStatus) params.set('linkedin_status', filterLinkedInStatus);
      
      const res = await axios.get(`${BACKEND_URL}/api/sourcing/admin/techniques?${params.toString()}`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      setTechniques(res.data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load techniques", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/sourcing/admin/stats`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this technique?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/sourcing/admin/techniques/${id}`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      toast({ title: "Deleted", description: "Technique removed" });
      loadTechniques();
      loadStats();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    }
  };

  const handleClone = async (technique) => {
    try {
      await axios.post(`${BACKEND_URL}/api/sourcing/admin/techniques/${technique.id}/clone`, {
        title: `${technique.title} (Copy)`,
        country: technique.country
      }, { headers: { 'Authorization': `Basic ${auth}` } });
      toast({ title: "Cloned", description: "Technique duplicated" });
      loadTechniques();
    } catch (error) {
      toast({ title: "Error", description: "Failed to clone", variant: "destructive" });
    }
  };

  const handleBulkStatusUpdate = async (ids, status) => {
    try {
      await axios.post(`${BACKEND_URL}/api/sourcing/admin/bulk/update-status`, {
        ids, status
      }, { headers: { 'Authorization': `Basic ${auth}` } });
      toast({ title: "Updated", description: `${ids.length} techniques updated` });
      loadTechniques();
      loadStats();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update", variant: "destructive" });
    }
  };

  // Filtered techniques
  const filteredTechniques = techniques.filter(t => {
    if (searchQuery && !t.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  // Paginated techniques
  const paginatedTechniques = filteredTechniques.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(filteredTechniques.length / itemsPerPage);

  // Render based on view mode
  if (viewMode === 'create' || viewMode === 'edit') {
    return (
      <TechniqueEditor
        auth={auth}
        technique={selectedTechnique}
        onSave={() => {
          setViewMode('list');
          setSelectedTechnique(null);
          loadTechniques();
          loadStats();
        }}
        onCancel={() => {
          setViewMode('list');
          setSelectedTechnique(null);
        }}
      />
    );
  }

  if (viewMode === 'calendar') {
    return (
      <CalendarView
        auth={auth}
        onBack={() => setViewMode('list')}
        onEdit={(tech) => {
          setSelectedTechnique(tech);
          setViewMode('edit');
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-blue-600">{stats.total}</div>
              <div className="text-sm text-slate-600">Total</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-green-600">{stats.published}</div>
              <div className="text-sm text-slate-600">Published</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-yellow-600">{stats.drafts}</div>
              <div className="text-sm text-slate-600">Drafts</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-purple-600">{stats.scheduled_linkedin}</div>
              <div className="text-sm text-slate-600">LinkedIn Scheduled</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-emerald-600">{stats.evergreen}</div>
              <div className="text-sm text-slate-600">Evergreen</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-slate-600">{stats.total_views}</div>
              <div className="text-sm text-slate-600">Total Views</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Action Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setViewMode('create')} style={{ backgroundColor: '#d4af37' }}>
                <Plus className="h-4 w-4 mr-2" />
                New Technique
              </Button>
              <Button variant="outline" onClick={() => setViewMode('calendar')}>
                <Calendar className="h-4 w-4 mr-2" />
                Calendar View
              </Button>
              <Button
                onClick={handleSyncToToday}
                disabled={syncing}
                className="bg-[#d4af37] text-[#1a2332] hover:bg-[#c9a227] font-semibold"
                data-testid="sync-to-today-button"
              >
                {syncing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Zap className="h-4 w-4 mr-2" />
                )}
                {syncing ? 'Syncing...' : 'Sync to Today'}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleSeedProduction}
                disabled={seeding}
                className="bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
              >
                {seeding ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Rocket className="h-4 w-4 mr-2" />
                )}
                {seeding ? 'Seeding...' : 'Seed to Production'}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleSeedFeb2026}
                disabled={seedingFeb2026}
                className="bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
              >
                {seedingFeb2026 ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Rocket className="h-4 w-4 mr-2" />
                )}
                {seedingFeb2026 ? 'Seeding...' : 'Seed Feb 2026'}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleSeedMarch2026}
                disabled={seedingMarch2026}
                className="bg-indigo-50 border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                data-testid="seed-march-2026-button"
              >
                {seedingMarch2026 ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Rocket className="h-4 w-4 mr-2" />
                )}
                {seedingMarch2026 ? 'Seeding...' : 'Seed March 2026'}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleRebalance}
                disabled={rebalancing}
                className="bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100"
                data-testid="rebalance-schedule-button"
              >
                {rebalancing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Zap className="h-4 w-4 mr-2" />
                )}
                {rebalancing ? 'Balancing...' : 'Re-balance Schedule'}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleSeedToCurrentDate}
                disabled={seedingCurrentDate}
                className="bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
              >
                {seedingCurrentDate ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Zap className="h-4 w-4 mr-2" />
                )}
                {seedingCurrentDate ? 'Updating...' : 'Publish to Today'}
              </Button>
              <Button variant="outline" onClick={loadTechniques}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
            
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search techniques..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-48"
                />
              </div>
              
              <select
                value={filterCountry}
                onChange={(e) => { setFilterCountry(e.target.value); loadTechniques(); }}
                className="border rounded-md px-3 py-2 text-sm"
              >
                <option value="">All Countries</option>
                {COUNTRIES.map(c => <option key={c} value={c}>{countryFlags[c]} {c}</option>)}
              </select>
              
              <select
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); loadTechniques(); }}
                className="border rounded-md px-3 py-2 text-sm"
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Techniques List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Sourcing Techniques ({filteredTechniques.length})</span>
            <div className="flex gap-2">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : filteredTechniques.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No techniques found</p>
              <Button onClick={() => setViewMode('create')} className="mt-4">
                Create your first technique
              </Button>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left p-3">Title</th>
                      <th className="text-left p-3">Country</th>
                      <th className="text-left p-3">Theme</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">LinkedIn</th>
                      <th className="text-left p-3">Views</th>
                      <th className="text-right p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTechniques.map((tech) => (
                      <tr key={tech.id} className="border-b hover:bg-slate-50">
                        <td className="p-3">
                          <div className="font-medium line-clamp-1 max-w-xs">{tech.title}</div>
                          <div className="text-xs text-slate-500">
                            {tech.role_categories?.slice(0, 2).join(', ')}
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-lg mr-1">{countryFlags[tech.country]}</span>
                          {tech.country}
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-xs">
                            {tech.sourcing_theme}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <Badge className={tech.status === 'published' ? 'bg-green-500' : 'bg-yellow-500'}>
                            {tech.status}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-xs">
                            {tech.linkedin?.status || 'draft'}
                          </Badge>
                          {tech.linkedin?.scheduled_date && (
                            <div className="text-xs text-slate-500 mt-1">
                              {new Date(tech.linkedin.scheduled_date).toLocaleDateString()}
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-center">{tech.page_views || 0}</td>
                        <td className="p-3">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(`/sourcing-hub/${tech.slug}`, '_blank')}
                              title="View"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedTechnique(tech); setViewMode('edit'); }}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleClone(tech)}
                              title="Clone"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(tech.id)}
                              className="text-red-500 hover:text-red-700"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-slate-500">
                    Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredTechniques.length)} of {filteredTechniques.length}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="px-3 py-1 text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Technique Editor Component
const TechniqueEditor = ({ auth, technique, onSave, onCancel }) => {
  const { toast } = useToast();
  const isEdit = !!technique;
  
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    title: technique?.title || '',
    country: technique?.country || 'India',
    role_categories: technique?.role_categories || [],
    sourcing_theme: technique?.sourcing_theme || 'Foundations',
    difficulty_level: technique?.difficulty_level || 'Beginner',
    target_audience: technique?.target_audience || ['Recruiter'],
    value_score: technique?.value_score || 5,
    read_time: technique?.read_time || 5,
    sections: technique?.sections || {
      problem_statement: { enabled: true, content: '' },
      technique_explanation: { enabled: true, content: '' },
      step_by_step: { enabled: true, content: '' },
      tools_platforms: { enabled: true, content: '' },
      boolean_strings: { enabled: false, content: '' },
      pro_tips: { enabled: true, content: '' },
      common_mistakes: { enabled: true, content: '' },
      best_roles: { enabled: true, content: '' }
    },
    linkedin: technique?.linkedin || {
      short_post: '',
      long_post: '',
      hashtags: [],
      image_url: '',
      image_prompt: '',
      scheduled_date: '',
      status: 'draft'
    },
    tags: technique?.tags || [],
    is_evergreen: technique?.is_evergreen ?? true,
    is_high_performing: technique?.is_high_performing || false,
    needs_update: technique?.needs_update || false,
    internal_notes: technique?.internal_notes || '',
    status: technique?.status || 'draft'
  });

  const [activeTab, setActiveTab] = useState('basic');
  const [tagsInput, setTagsInput] = useState(formData.tags.join(', '));
  const [hashtagsInput, setHashtagsInput] = useState(formData.linkedin.hashtags.join(', '));

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast({ title: "Error", description: "Title is required", variant: "destructive" });
      return;
    }
    
    try {
      setLoading(true);
      
      const data = {
        ...formData,
        tags: tagsInput.split(',').map(t => t.trim()).filter(Boolean),
        linkedin: {
          ...formData.linkedin,
          hashtags: hashtagsInput.split(',').map(t => t.trim()).filter(Boolean)
        }
      };
      
      if (isEdit) {
        await axios.put(`${BACKEND_URL}/api/sourcing/admin/techniques/${technique.id}`, data, {
          headers: { 'Authorization': `Basic ${auth}` }
        });
        toast({ title: "Updated", description: "Technique saved successfully" });
      } else {
        await axios.post(`${BACKEND_URL}/api/sourcing/admin/techniques`, data, {
          headers: { 'Authorization': `Basic ${auth}` }
        });
        toast({ title: "Created", description: "Technique created successfully" });
      }
      onSave();
    } catch (error) {
      toast({ title: "Error", description: "Failed to save technique", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // AI Generation functions
  const generateContent = async () => {
    try {
      setGenerating(prev => ({ ...prev, content: true }));
      const res = await axios.post(`${BACKEND_URL}/api/sourcing/admin/generate/content`, {
        title: formData.title,
        country: formData.country,
        role_category: formData.role_categories[0] || 'IT',
        theme: formData.sourcing_theme
      }, { headers: { 'Authorization': `Basic ${auth}` } });
      
      if (res.data.success && res.data.content) {
        const content = res.data.content;
        setFormData(prev => ({
          ...prev,
          sections: {
            problem_statement: { enabled: true, content: content.problem_statement || '' },
            technique_explanation: { enabled: true, content: content.technique_explanation || '' },
            step_by_step: { enabled: true, content: content.step_by_step || '' },
            tools_platforms: { enabled: true, content: content.tools_platforms || '' },
            boolean_strings: { enabled: !!content.boolean_strings, content: content.boolean_strings || '' },
            pro_tips: { enabled: true, content: content.pro_tips || '' },
            common_mistakes: { enabled: true, content: content.common_mistakes || '' },
            best_roles: { enabled: true, content: content.best_roles || '' }
          }
        }));
        toast({ title: "Generated", description: "Content generated successfully" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to generate content", variant: "destructive" });
    } finally {
      setGenerating(prev => ({ ...prev, content: false }));
    }
  };

  const generateLinkedIn = async (type) => {
    try {
      setGenerating(prev => ({ ...prev, [type]: true }));
      const res = await axios.post(`${BACKEND_URL}/api/sourcing/admin/generate/linkedin`, {
        title: formData.title,
        country: formData.country,
        theme: formData.sourcing_theme,
        content_summary: formData.sections.problem_statement?.content || '',
        post_type: type
      }, { headers: { 'Authorization': `Basic ${auth}` } });
      
      if (res.data.success) {
        setFormData(prev => ({
          ...prev,
          linkedin: {
            ...prev.linkedin,
            [type === 'short' ? 'short_post' : 'long_post']: res.data.content
          }
        }));
        toast({ title: "Generated", description: `${type} post generated` });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to generate", variant: "destructive" });
    } finally {
      setGenerating(prev => ({ ...prev, [type]: false }));
    }
  };

  const generateHashtags = async () => {
    try {
      setGenerating(prev => ({ ...prev, hashtags: true }));
      const res = await axios.post(`${BACKEND_URL}/api/sourcing/admin/generate/hashtags`, {
        title: formData.title,
        country: formData.country,
        theme: formData.sourcing_theme,
        category: formData.role_categories[0] || 'IT'
      }, { headers: { 'Authorization': `Basic ${auth}` } });
      
      if (res.data.success) {
        setHashtagsInput(res.data.hashtags.join(', '));
        toast({ title: "Generated", description: "Hashtags generated" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to generate hashtags", variant: "destructive" });
    } finally {
      setGenerating(prev => ({ ...prev, hashtags: false }));
    }
  };

  const generateImagePrompt = async () => {
    try {
      setGenerating(prev => ({ ...prev, imagePrompt: true }));
      const res = await axios.post(`${BACKEND_URL}/api/sourcing/admin/generate/image-prompt`, {
        title: formData.title,
        theme: formData.sourcing_theme,
        country: formData.country
      }, { headers: { 'Authorization': `Basic ${auth}` } });
      
      if (res.data.success) {
        setFormData(prev => ({
          ...prev,
          linkedin: { ...prev.linkedin, image_prompt: res.data.image_prompt }
        }));
        toast({ title: "Generated", description: "Image prompt generated" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to generate", variant: "destructive" });
    } finally {
      setGenerating(prev => ({ ...prev, imagePrompt: false }));
    }
  };

  const generateImage = async () => {
    if (!formData.linkedin.image_prompt) {
      toast({ title: "Error", description: "Generate image prompt first", variant: "destructive" });
      return;
    }
    
    try {
      setGenerating(prev => ({ ...prev, image: true }));
      const res = await axios.post(`${BACKEND_URL}/api/sourcing/admin/generate/image`, {
        prompt: formData.linkedin.image_prompt
      }, { headers: { 'Authorization': `Basic ${auth}` } });
      
      if (res.data.success) {
        setFormData(prev => ({
          ...prev,
          linkedin: { ...prev.linkedin, image_url: res.data.image_url }
        }));
        toast({ title: "Generated", description: "Image generated successfully" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to generate image", variant: "destructive" });
    } finally {
      setGenerating(prev => ({ ...prev, image: false }));
    }
  };

  const sectionLabels = {
    problem_statement: 'Problem Statement',
    technique_explanation: 'Technique Explanation',
    step_by_step: 'Step-by-Step Method',
    tools_platforms: 'Free Tools & Platforms',
    boolean_strings: 'Boolean / Search Strings',
    pro_tips: 'Pro Tips & Insights',
    common_mistakes: 'Common Mistakes',
    best_roles: 'Best Roles to Apply'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onCancel}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h2 className="text-xl font-bold">
            {isEdit ? 'Edit Technique' : 'Create New Technique'}
          </h2>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} style={{ backgroundColor: '#d4af37' }}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {isEdit ? 'Save Changes' : 'Create Technique'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b">
        {['basic', 'content', 'linkedin', 'settings'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === tab
                ? 'border-amber-500 text-amber-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <Card>
        <CardContent className="p-6">
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Boolean Search for IT Roles in India"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Country *</label>
                  <select
                    value={formData.country}
                    onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    {COUNTRIES.map(c => (
                      <option key={c} value={c}>{countryFlags[c]} {c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Sourcing Theme *</label>
                  <select
                    value={formData.sourcing_theme}
                    onChange={(e) => setFormData(prev => ({ ...prev, sourcing_theme: e.target.value }))}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    {SOURCING_THEMES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Difficulty</label>
                  <select
                    value={formData.difficulty_level}
                    onChange={(e) => setFormData(prev => ({ ...prev, difficulty_level: e.target.value }))}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    {DIFFICULTY_LEVELS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Role Categories</label>
                <div className="flex flex-wrap gap-2">
                  {ROLE_CATEGORIES.map(cat => (
                    <Badge
                      key={cat}
                      variant={formData.role_categories.includes(cat) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          role_categories: prev.role_categories.includes(cat)
                            ? prev.role_categories.filter(c => c !== cat)
                            : [...prev.role_categories, cat]
                        }));
                      }}
                    >
                      {cat}
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Target Audience</label>
                <div className="flex flex-wrap gap-2">
                  {TARGET_AUDIENCES.map(aud => (
                    <Badge
                      key={aud}
                      variant={formData.target_audience.includes(aud) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          target_audience: prev.target_audience.includes(aud)
                            ? prev.target_audience.filter(a => a !== aud)
                            : [...prev.target_audience, aud]
                        }));
                      }}
                    >
                      {aud}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Value Score (1-10)</label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.value_score}
                    onChange={(e) => setFormData(prev => ({ ...prev, value_score: parseInt(e.target.value) || 5 }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Read Time (minutes)</label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.read_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, read_time: parseInt(e.target.value) || 5 }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tags (comma separated)</label>
                <Input
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="e.g., boolean, linkedin, passive candidates"
                />
              </div>
            </div>
          )}

          {activeTab === 'content' && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button
                  onClick={generateContent}
                  disabled={!formData.title || generating.content}
                  variant="outline"
                >
                  {generating.content ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Generate All Content with AI
                </Button>
              </div>

              {Object.entries(sectionLabels).map(([key, label]) => (
                <div key={key} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.sections[key]?.enabled}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          sections: {
                            ...prev.sections,
                            [key]: { ...prev.sections[key], enabled: e.target.checked }
                          }
                        }))}
                      />
                      {label}
                    </label>
                  </div>
                  {formData.sections[key]?.enabled && (
                    <Textarea
                      value={formData.sections[key]?.content || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        sections: {
                          ...prev.sections,
                          [key]: { ...prev.sections[key], content: e.target.value }
                        }
                      }))}
                      placeholder={`Enter ${label.toLowerCase()}...`}
                      rows={key === 'boolean_strings' ? 6 : 4}
                      className={key === 'boolean_strings' ? 'font-mono text-sm' : ''}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'linkedin' && (
            <div className="space-y-6">
              {/* Short Post */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Short LinkedIn Post (under 300 words)</label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => generateLinkedIn('short')}
                    disabled={generating.short}
                  >
                    {generating.short ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  </Button>
                </div>
                <Textarea
                  value={formData.linkedin.short_post}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    linkedin: { ...prev.linkedin, short_post: e.target.value }
                  }))}
                  rows={6}
                  placeholder="Generate or write a short LinkedIn post..."
                />
                <div className="text-xs text-slate-500 mt-1">
                  {formData.linkedin.short_post.length} characters
                </div>
              </div>

              {/* Long Post */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Long LinkedIn Post / Carousel Caption</label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => generateLinkedIn('long')}
                    disabled={generating.long}
                  >
                    {generating.long ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  </Button>
                </div>
                <Textarea
                  value={formData.linkedin.long_post}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    linkedin: { ...prev.linkedin, long_post: e.target.value }
                  }))}
                  rows={10}
                  placeholder="Generate or write a detailed LinkedIn post..."
                />
              </div>

              {/* Hashtags */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Hashtags</label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={generateHashtags}
                    disabled={generating.hashtags}
                  >
                    {generating.hashtags ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  </Button>
                </div>
                <Input
                  value={hashtagsInput}
                  onChange={(e) => setHashtagsInput(e.target.value)}
                  placeholder="recruiting, sourcing, talentacquisition, ..."
                />
              </div>

              {/* Image */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">Image Prompt</label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={generateImagePrompt}
                      disabled={generating.imagePrompt}
                    >
                      {generating.imagePrompt ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    </Button>
                  </div>
                  <Textarea
                    value={formData.linkedin.image_prompt}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      linkedin: { ...prev.linkedin, image_prompt: e.target.value }
                    }))}
                    rows={4}
                    placeholder="AI image generation prompt..."
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={generateImage}
                    disabled={generating.image || !formData.linkedin.image_prompt}
                    className="mt-2"
                  >
                    {generating.image ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Image className="h-4 w-4 mr-2" />}
                    Generate Image
                  </Button>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Image URL</label>
                  <Input
                    value={formData.linkedin.image_url}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      linkedin: { ...prev.linkedin, image_url: e.target.value }
                    }))}
                    placeholder="https://..."
                  />
                  {formData.linkedin.image_url && (
                    <img
                      src={formData.linkedin.image_url}
                      alt="LinkedIn preview"
                      className="mt-2 rounded-lg max-h-40 object-cover"
                    />
                  )}
                </div>
              </div>

              {/* Schedule */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">LinkedIn Status</label>
                  <select
                    value={formData.linkedin.status}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      linkedin: { ...prev.linkedin, status: e.target.value }
                    }))}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    {LINKEDIN_STATUSES.map(s => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Scheduled Date</label>
                  <Input
                    type="datetime-local"
                    value={formData.linkedin.scheduled_date ? formData.linkedin.scheduled_date.slice(0, 16) : ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      linkedin: { ...prev.linkedin, scheduled_date: e.target.value ? new Date(e.target.value).toISOString() : '' }
                    }))}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium mb-2 block">Publication Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_evergreen}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_evergreen: e.target.checked }))}
                  />
                  <span>♻️ Evergreen Content (can be reused anytime)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_high_performing}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_high_performing: e.target.checked }))}
                  />
                  <span>⭐ High Performing</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.needs_update}
                    onChange={(e) => setFormData(prev => ({ ...prev, needs_update: e.target.checked }))}
                  />
                  <span>⚠️ Needs Update</span>
                </label>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Internal Notes (admin only)</label>
                <Textarea
                  value={formData.internal_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, internal_notes: e.target.value }))}
                  rows={4}
                  placeholder="Notes visible only in admin panel..."
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Calendar View Component
const CalendarView = ({ auth, onBack, onEdit }) => {
  const { toast } = useToast();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCalendarData();
  }, [currentDate]);

  const loadCalendarData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/sourcing/admin/calendar`, {
        params: {
          year: currentDate.getFullYear(),
          month: currentDate.getMonth() + 1
        },
        headers: { 'Authorization': `Basic ${auth}` }
      });
      setCalendarData(res.data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load calendar", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty slots for days before first day of month
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    // Add all days of month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d));
    }
    
    return days;
  };

  const getScheduledForDate = (date) => {
    if (!date) return [];
    const dateKey = date.toISOString().split('T')[0];
    return calendarData.schedule?.[dateKey] || [];
  };

  const isWeekend = (date) => {
    if (!date) return false;
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const getExpectedCountry = (date) => {
    if (!date || isWeekend(date)) return null;
    const dayOfWeek = date.getDay();
    return calendarData.country_mapping?.[dayOfWeek] || null;
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h2 className="text-xl font-bold">Content Calendar</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="font-medium px-4">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Country Legend */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="font-medium">Weekday → Country:</span>
            <span>Mon: 🇮🇳 India</span>
            <span>Tue: 🇺🇸 USA</span>
            <span>Wed: 🇬🇧 UK</span>
            <span>Thu: 🌍 EMEA</span>
            <span>Fri: 🇦🇺 Australia</span>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {/* Day headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center font-medium text-slate-600 py-2 text-sm">
                  {day}
                </div>
              ))}
              
              {/* Calendar cells */}
              {getDaysInMonth().map((date, index) => {
                const scheduled = getScheduledForDate(date);
                const expectedCountry = getExpectedCountry(date);
                const weekend = isWeekend(date);
                
                return (
                  <div
                    key={index}
                    className={`min-h-24 border rounded p-1 ${
                      !date ? 'bg-slate-50' :
                      weekend ? 'bg-slate-100' :
                      'bg-white hover:bg-slate-50'
                    }`}
                  >
                    {date && (
                      <>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-sm font-medium ${weekend ? 'text-slate-400' : ''}`}>
                            {date.getDate()}
                          </span>
                          {expectedCountry && (
                            <span className="text-xs">{countryFlags[expectedCountry]}</span>
                          )}
                        </div>
                        <div className="space-y-1">
                          {scheduled.slice(0, 3).map((tech, i) => (
                            <div
                              key={i}
                              className={`text-xs p-1 rounded truncate cursor-pointer ${
                                tech.status === 'published' ? 'bg-green-100 text-green-800' :
                                tech.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}
                              title={tech.title}
                              onClick={() => onEdit(tech)}
                            >
                              {countryFlags[tech.country]} {tech.title?.slice(0, 20)}...
                            </div>
                          ))}
                          {scheduled.length > 3 && (
                            <div className="text-xs text-slate-500">
                              +{scheduled.length - 3} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SourcingKnowledgeManager;
